import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { useToast } from './ToastContainer';
import UserAvatar from './UserAvatar';
import '../styles/UserManagement.css';

interface User {
  id: number;
  email: string;
  name: string;
  picture?: string;
  role: 'user' | 'admin' | 'supervisor' | 'limited_admin';
  created_at: string;
  assignments?: QuizAssignment[];
}

interface QuizAssignment {
  quiz_id: number;
  quiz_title: string;
  assigned_at: string;
}

interface Quiz {
  id: number;
  title: string;
}

const UserManagement: React.FC = () => {
  const { showToast } = useToast();
  const [users, setUsers] = useState<User[]>([]);
  const [quizzes, setQuizzes] = useState<Quiz[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [selectedQuizIds, setSelectedQuizIds] = useState<number[]>([]);
  const [updatingUserId, setUpdatingUserId] = useState<number | null>(null);
  const [roleFilter, setRoleFilter] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState<string>('');

  useEffect(() => {
    fetchUsers();
    fetchQuizzes();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await api.get('/api/user-management/users');
      setUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
      showToast('Failed to fetch users', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchQuizzes = async () => {
    try {
      const response = await api.get('/api/quizzes');
      setQuizzes(response.data);
    } catch (error) {
      console.error('Error fetching quizzes:', error);
    }
  };

  const handleRoleChange = async (userId: number, newRole: string) => {
    try {
      setUpdatingUserId(userId);
      await api.patch(`/api/user-management/users/${userId}/role`, { role: newRole });
      showToast('User role updated successfully', 'success');
      await fetchUsers(); // Refresh the list
    } catch (error) {
      console.error('Error updating user role:', error);
      showToast('Failed to update user role', 'error');
    } finally {
      setUpdatingUserId(null);
    }
  };

  const handleAssignQuizzes = (user: User) => {
    setSelectedUser(user);
    // Pre-select currently assigned quizzes
    const currentAssignments = user.assignments?.map(a => a.quiz_id) || [];
    setSelectedQuizIds(currentAssignments);
    setShowAssignModal(true);
  };

  const handleSaveAssignments = async () => {
    if (!selectedUser) return;

    try {
      await api.post(`/api/user-management/users/${selectedUser.id}/assignments`, {
        quizIds: selectedQuizIds
      });
      showToast('Quiz assignments updated successfully', 'success');
      setShowAssignModal(false);
      await fetchUsers(); // Refresh the list
    } catch (error) {
      console.error('Error updating assignments:', error);
      showToast('Failed to update quiz assignments', 'error');
    }
  };

  const handleRemoveAssignment = async (userId: number, quizId: number) => {
    try {
      await api.delete(`/api/user-management/users/${userId}/assignments/${quizId}`);
      showToast('Assignment removed successfully', 'success');
      await fetchUsers(); // Refresh the list
    } catch (error) {
      console.error('Error removing assignment:', error);
      showToast('Failed to remove assignment', 'error');
    }
  };

  const toggleQuizSelection = (quizId: number) => {
    setSelectedQuizIds(prev => 
      prev.includes(quizId) 
        ? prev.filter(id => id !== quizId)
        : [...prev, quizId]
    );
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Filter users based on selected role and search query
  const filteredUsers = (roleFilter === 'all' 
    ? users 
    : users.filter(user => user.role === roleFilter)
  ).filter(user => {
    if (!searchQuery.trim()) return true;
    
    const query = searchQuery.toLowerCase().trim();
    const name = user.name?.toLowerCase() || '';
    const email = user.email?.toLowerCase() || '';
    const userId = user.id?.toString() || '';
    
    // Search in name, email, and user ID
    return name.includes(query) || 
           email.includes(query) ||
           userId.includes(query);
  });

  const handleRoleFilterChange = (newFilter: string) => {
    setRoleFilter(newFilter);
  };

  const clearFilter = () => {
    setRoleFilter('all');
  };

  if (loading) {
    return <div className="user-management-loading">Loading users...</div>;
  }

  return (
    <div className="user-management">
      <div className="user-management-header">
        <h2>User Management</h2>
        <div className="role-filter-bubbles">
          <div 
            className={`role-bubble ${roleFilter === 'all' ? 'active' : ''}`}
            onClick={() => handleRoleFilterChange('all')}
          >
            <span className="role-name">All Users</span>
            <span className="role-count">{users.length}</span>
          </div>
          {['user', 'supervisor', 'limited_admin', 'admin']
            .map(role => ({
              role,
              count: users.filter(u => u.role === role).length
            }))
            .sort((a, b) => b.count - a.count)
            .map(({ role, count }) => (
              <div 
                key={role}
                className={`role-bubble ${roleFilter === role ? 'active' : ''}`}
                onClick={() => handleRoleFilterChange(role)}
              >
                <span className="role-name">
                  {role === 'limited_admin' ? 'CES Admins' : 
                   role === 'user' ? 'Users' :
                   role.charAt(0).toUpperCase() + role.slice(1) + 's'}
                </span>
                <span className="role-count">{count}</span>
              </div>
            ))}
        </div>
      </div>
      
      <div className="users-table">
        <div style={{ 
          display: 'flex', 
          justifyContent: 'flex-start', 
          alignItems: 'center', 
          marginBottom: '20px'
        }}>
          <div style={{ position: 'relative', width: '300px' }}>
            <input
              type="text"
              placeholder="Search by name or email"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{
                width: '100%',
                padding: '8px 12px 8px 40px',
                borderRadius: '6px',
                border: '1px solid #ddd',
                fontSize: '14px',
                outline: 'none',
                transition: 'border-color 0.2s'
              }}
              onFocus={(e) => e.target.style.borderColor = '#4CAF50'}
              onBlur={(e) => e.target.style.borderColor = '#ddd'}
            />
            <svg
              style={{
                position: 'absolute',
                left: '12px',
                top: '50%',
                transform: 'translateY(-50%)',
                width: '16px',
                height: '16px',
                color: '#666',
                pointerEvents: 'none'
              }}
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              viewBox="0 0 24 24"
            >
              <circle cx="11" cy="11" r="8"></circle>
              <path d="m21 21-4.35-4.35"></path>
            </svg>
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                style={{
                  position: 'absolute',
                  right: '8px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '18px',
                  color: '#999',
                  padding: '4px 8px',
                  borderRadius: '4px',
                  transition: 'background-color 0.2s'
                }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f0f0f0'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                title="Clear search"
              >
                ×
              </button>
            )}
          </div>
        </div>
        
        {searchQuery && filteredUsers.length === 0 ? (
          <div style={{ 
            padding: '40px 20px', 
            textAlign: 'center', 
            color: '#666',
            backgroundColor: '#f9f9f9',
            borderRadius: '6px',
            marginBottom: '20px'
          }}>
            <p style={{ margin: 0, fontSize: '16px', marginBottom: '15px' }}>
              No users found matching "{searchQuery}"
            </p>
            <button
              onClick={() => setSearchQuery('')}
              className="btn btn-outline btn-small"
            >
              Clear Search
            </button>
          </div>
        ) : (
          <table>
          <thead>
            <tr>
              <th>User</th>
              <th>Email</th>
              <th>Role</th>
              <th>Assigned Quizzes</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredUsers.map(user => (
              <tr key={user.id}>
                <td>
                  <div className="user-info">
                    <UserAvatar name={user.name} picture={user.picture} className="user-avatar" />
                    <span>{user.name}</span>
                  </div>
                </td>
                <td>{user.email}</td>
                <td>
                  <select 
                    value={user.role} 
                    onChange={(e) => handleRoleChange(user.id, e.target.value)}
                    className="role-select"
                    disabled={updatingUserId === user.id}
                  >
                    <option value="user">User</option>
                    <option value="supervisor">Supervisor</option>
                    <option value="limited_admin">CES Admin</option>
                    <option value="admin">Admin</option>
                  </select>
                </td>
                <td>
                  {user.role === 'supervisor' || user.role === 'limited_admin' ? (
                    <div className="assignments">
                      {user.assignments && user.assignments.length > 0 ? (
                        <ul className="assignment-list">
                          {user.assignments.map(assignment => (
                            <li key={assignment.quiz_id}>
                              {assignment.quiz_title}
                              <button 
                                className="remove-btn"
                                onClick={() => handleRemoveAssignment(user.id, assignment.quiz_id)}
                                title="Remove assignment"
                              >
                                ×
                              </button>
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <span className="no-assignments">No assignments</span>
                      )}
                    </div>
                  ) : (
                    <span className="not-applicable">
                      {user.role === 'admin' ? 'All quizzes' : 'N/A'}
                    </span>
                  )}
                </td>
                <td>
                  {(user.role === 'supervisor' || user.role === 'limited_admin') && (
                    <button 
                      className="btn btn-sm btn-primary"
                      onClick={() => handleAssignQuizzes(user)}
                    >
                      Manage Assignments
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        )}
      </div>

      {/* Assignment Modal */}
      {showAssignModal && selectedUser && (
        <div className="modal-overlay" onClick={() => setShowAssignModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Assign Quizzes to {selectedUser.name}</h3>
            
            <div className="quiz-selection">
              {quizzes.map(quiz => (
                <label key={quiz.id} className="quiz-checkbox">
                  <input
                    type="checkbox"
                    checked={selectedQuizIds.includes(quiz.id)}
                    onChange={() => toggleQuizSelection(quiz.id)}
                  />
                  <span>{quiz.title}</span>
                </label>
              ))}
            </div>

            <div className="modal-actions">
              <button 
                className="btn btn-secondary"
                onClick={() => setShowAssignModal(false)}
              >
                Cancel
              </button>
              <button 
                className="btn btn-primary"
                onClick={handleSaveAssignments}
              >
                Save Assignments
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagement;
